import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { BasicForm, SportForm, RestrictedForm, LivestreamsForm, EmailsForm } from "./common-forms";
import { useQuery } from "components/graphql/query/use-query";
import { getEventTypeQuery } from "./utils";
import { useLoadingContext } from "components/loading-spinner-overlay";
import { useApolloContext } from "components/graphql";
import { injectIntl } from "react-intl";
import getMixerSlotsData from "./mixer-slots-query";
import { getElementsVisibleOnMixer } from "pages/graphicset/utils";
import { connect } from "react-redux";
import { updateLivestreamsActionCreator } from "../../../actions/common-actions";
import { compose } from "redux";
import LocalRecEvent from "./common-forms/local-rec-event";

const CommonForm = ({
  customer,
  isAuthorizedRecording,
  isNewForm = false,
  values,
  handleChange,
  showAdminFields = false,
  canEditEventType = false,
  canEditRoomTemplate = false,
  isSportEvent,
  setIsSportEvent,
  eventCustomerId,
  eventToEdit,
  intl,
  showTestTemplates,
  updateLivestreams,
  event,
  showLocalRecEventFields,
  setShowLocalRecEventFields,
}) => {
  const eventTypeQuery = useMemo(() => getEventTypeQuery(values.typeId), [values.typeId]);
  const { data } = useQuery(eventTypeQuery);
  const { setLoading } = useLoadingContext();
  const client = useApolloContext();
  const [mixerMessage, setMixerMessage] = useState(null);

  useEffect(
    () => {
      if (canEditEventType) {
        const isSportEventSelected = values.typeId ? data && data.eventType && data.eventType.isSportEvent : false;
        if (isSportEvent !== isSportEventSelected) {
          setIsSportEvent(isSportEventSelected);
        }
      }
    },
    [canEditEventType, data, isSportEvent, setIsSportEvent, values.typeId],
  );

  useEffect(
    () => {
      if (values.roomTemplateId && values.graphicSetId) {
        setLoading(true);
        client
          .query(
            getMixerSlotsData({
              roomTemplateId: values.roomTemplateId,
              graphicSetId: values.graphicSetId,
              contractId: values.contractId,
            }),
          )
          .then(({ data, errors }) => {
            if (errors) {
              throw new Error(errors);
            }

            if (!data) {
              throw new Error("Empty data");
            }

            return data.mixerSlotsData;
          })
          .then(data => {
            const elementsVisibleOnMixer = getElementsVisibleOnMixer(data.graphicSet);
            if (elementsVisibleOnMixer && elementsVisibleOnMixer > data.roomTemplate.maxWindowMixer) {
              setMixerMessage(
                intl.formatHTMLMessage(
                  {
                    id: "event.mixer.slot.error",
                  },
                  {
                    maxSlots: data.roomTemplate.maxWindowMixer,
                    currentSlots: elementsVisibleOnMixer,
                    graphicsetUrl: `/graphicset/edit/${data.graphicSet.id}/customerId/${data.graphicSet.customerId}`,
                    graphicsetName: data.graphicSet.name,
                    maxSlotsIfProgrammable: data.roomTemplate.maxWindowMixer - 1,
                  },
                ),
              );
            } else {
              setMixerMessage(null);
            }
          })
          .catch(error => {
            console.error(error);
            setMixerMessage(null);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
    [client, intl, setLoading, values.contractId, values.graphicSetId, values.roomTemplateId],
  );

  useEffect(
    () => {
      if (values?.liveStreams) {
        updateLivestreams(values.liveStreams);
      }
    },
    [values?.liveStreams],
  );

  useEffect(
    () => {
      setShowLocalRecEventFields(values.isLocalRecEvent);
    },
    [values?.isLocalRecEvent],
  );

  useEffect(
    () => {
      if (!showLocalRecEventFields && !!values.localRecInfo && values.localRecInfo.userEmails.length) {
        setShowLocalRecEventFields(true);
      }
    },
    [values?.localRecInfo],
  );

  return (
    <>
      <BasicForm
        canEditEventType={canEditEventType}
        canEditRoomTemplate={canEditRoomTemplate}
        customer={customer}
        handleChange={handleChange}
        isNewForm={isNewForm}
        isAuthorizedRecording={isAuthorizedRecording}
        values={values}
        errorTextMixerSlots={mixerMessage}
        eventToEdit={eventToEdit}
        showTestTemplates={showTestTemplates}
        showLocalRecEventFields={showLocalRecEventFields}
      />
      {showLocalRecEventFields && (
        <LocalRecEvent
          eventToEdit={eventToEdit}
          isNewForm={isNewForm}
          eventCustomerId={eventCustomerId}
          handleChange={handleChange}
          values={values}
          event={event}
        />
      )}
      {showAdminFields && (
        <RestrictedForm handleChange={handleChange} values={values} />
      )}
      {isSportEvent && !showLocalRecEventFields && (
        <SportForm
          eventToEdit={eventToEdit}
          isNewForm={isNewForm}
          eventCustomerId={eventCustomerId}
          handleChange={handleChange}
          values={values}
        />
      )}
      {!showLocalRecEventFields && (
        <LivestreamsForm
          updateLivestreams={updateLivestreams}
          handleChange={handleChange}
          eventToEdit={eventToEdit}
          values={values}
        />
      )}
      <EmailsForm handleChange={handleChange} values={values} />
    </>
  );
};

CommonForm.propTypes = {
  /**
   * Indica si se puede editar la plantilla
   */
  canEditRoomTemplate: PropTypes.bool,
  /**
   * Indica si se puede editar el tipo de evento
   */
  canEditEventType: PropTypes.bool,
  /**
   * Indica si el tipo del evento seleccionado es deportivo
   */
  isSportEvent: PropTypes.bool,
  /**
   * Callback para cambiar si es evento deportivo
   */
  setIsSportEvent: PropTypes.func,
  /**
   * Indica si el formulario se está utilizando para crear un evento
   */
  isNewForm: PropTypes.bool,
  /**
   * Valores del formulario
   */
  values: PropTypes.object,
  /**
   * Muestra los inputs específicos de edición del admin
   */
  showAdminFields: PropTypes.bool,
  /**
   * Callback que se dispara con el cambio del input
   */
  handleChange: PropTypes.func,
  /**
   * Customer id del contrato del evento
   */
  eventCustomerId: PropTypes.string,
  /**
   * Indica si se muestran las plantillas de test
   */
  showTestTemplates: PropTypes.bool,
  /**
   * Objeto que contiene el valor localRecAvailable
   */
  customer: PropTypes.object,
};

const mapDispatchToProps = dispatch => ({
  updateLivestreams: data => dispatch(updateLivestreamsActionCreator(data)),
});

export default compose(
  injectIntl,
  connect(
    null,
    mapDispatchToProps,
  ),
)(CommonForm);
